export const TILE_COLORS = [
	{ value: "", title: "No color", color: "" },
	{ value: "blue", title: "Blue", color: "#123a6b" },
	{ value: "green", title: "Green", color: "#16502a" },
	{ value: "red", title: "Red", color: "#6b1717" },
	{ value: "purple", title: "Purple", color: "#47206b" },
	{ value: "orange", title: "Orange", color: "#6b3a0c" },
	{ value: "teal", title: "Teal", color: "#0c5257" }
];

// Value used in color filters for tiles without a color
export const NO_COLOR = "none";

export function tileColor(account, colorKey) {
	if (!colorKey) return "";
	var colors = (account && account.meta && account.meta.tile_colors) || {};
	return colors[colorKey] || "";
}

// Removes the tile color of colorKey from a (cloned) account
export function removeTileColor(account, colorKey) {
	if (!colorKey || !account.meta || !account.meta.tile_colors || !(colorKey in account.meta.tile_colors)) return account;
	var colors = Object.assign({}, account.meta.tile_colors);
	delete colors[colorKey];
	account.meta.tile_colors = colors;
	return account;
}

// Selected colors for a view, stored in account.meta.tile_color_filter[view]; empty means show all
export function tileColorFilter(account, view) {
	var filters = (account && account.meta && account.meta.tile_color_filter) || {};
	return filters[view] || [];
}

export function filterByTileColor(items, account, view, keyFn) {
	var filter = tileColorFilter(account, view);
	if (!filter.length) return items;
	return items.filter(item => filter.includes(tileColor(account, keyFn(item)) || NO_COLOR));
}

// Monitor2 tiles are stored in account.meta.monitor2_items as keys:
// "channel:<channel uuid>:<alternative>", "event:<event uuid>:<view>" or "instance:<instance id>"
export const MONITOR2_EVENT_VIEWS = [
	{ value: "source", title: "Source", icon: "input" },
	{ value: "preview", title: "Preview", icon: "preview" },
	{ value: "live", title: "Live", icon: "connected_tv" }
];

export function monitor2Items(account) {
	return (account && account.meta && account.meta.monitor2_items) || [];
}

export function monitor2ChannelKey(statusKey) {
	return "channel:" + statusKey;
}

export function monitor2EventKey(eventUuid, view) {
	return "event:" + eventUuid + ":" + view;
}

export function monitor2InstanceKey(instanceId) {
	return "instance:" + instanceId;
}

export function parseMonitor2Key(key) {
	var parts = String(key).split(":");
	if (parts[0] === "channel") return { type: "channel", uuid: parts[1], alternative: parts.slice(2).join(":") };
	if (parts[0] === "event") return { type: "event", uuid: parts[1], view: parts[2] };
	if (parts[0] === "instance") return { type: "instance", id: parts.slice(1).join(":") };
	return { type: "" };
}

export function monitor2ColorKey(key) {
	return "monitor2:" + key;
}

// Toggles keys in a (cloned) account; removed tiles also lose their tile color and tool selections
export function toggleMonitor2Items(account, keys, enable) {
	var items = monitor2Items(account).slice();
	keys.forEach(key => {
		var on = typeof(enable) == "boolean" ? enable : !items.includes(key);
		if (on && !items.includes(key)) items.push(key);
		if (!on && items.includes(key)) {
			items = items.filter(k => k !== key);
			removeTileColor(account, monitor2ColorKey(key));
			setMonitor2Tools(account, key, null);
		}
	});
	account.meta.monitor2_items = items;
	return account;
}

// KVideoArea layout of monitor2 in account.meta.monitor2_layout ({sizes, positions, group_rects, members,
// free_heights}); entries of removed tiles and groups are dropped by KVideoArea (prune_layout)
export function monitor2Layout(account) {
	var layout = account && account.meta && account.meta.monitor2_layout;
	return layout && typeof layout === "object" ? layout : {};
}

export function setMonitor2Layout(account, layout) {
	account.meta.monitor2_layout = layout;
	return account;
}

// Tool selections of monitor2 tiles in account.meta.monitor2_tools ({tile key: {diamond: true, ...}});
// tiles with every tool off have no entry
export function monitor2Tools(account, key) {
	var all = account && account.meta && account.meta.monitor2_tools;
	var state = all && typeof all === "object" ? all[key] : null;
	return state && typeof state === "object" ? state : null;
}

// state null or empty removes the entry
export function setMonitor2Tools(account, key, state) {
	var all = Object.assign({}, account.meta.monitor2_tools);
	if (state && Object.keys(state).length) all[key] = state;
	else delete all[key];
	if (Object.keys(all).length) account.meta.monitor2_tools = all;
	else delete account.meta.monitor2_tools;
	return account;
}

// Removes the tool selections of tiles that are no longer shown; true when something was removed
export function pruneMonitor2Tools(account, keys) {
	var all = (account.meta && account.meta.monitor2_tools) || {};
	var stale = Object.keys(all).filter(k => !keys.includes(k));
	stale.forEach(k => setMonitor2Tools(account, k, null));
	return stale.length > 0;
}

// Monitor2 groups are stored in account.meta.monitor2_groups as [{id, title, color, event, items}]:
// color is a TILE_COLORS value, event the uuid of an event group and items the videos placed in the group
// when it is created. Their size, position and videos are saved with the layout (monitor2_layout).
export function monitor2Groups(account) {
	return (account && account.meta && account.meta.monitor2_groups) || [];
}

export function addMonitor2Group(account, fields) {
	var groups = monitor2Groups(account).slice();
	var id = "g" + Date.now().toString(36) + Math.random().toString(36).slice(2, 6);
	groups.push(Object.assign({ id: id, title: "Group " + (groups.length + 1) }, fields));
	account.meta.monitor2_groups = groups;
	return account;
}

// Removes every monitor2 video (with its tile color) and group
export function clearMonitor2(account) {
	toggleMonitor2Items(account, monitor2Items(account).slice(), false);
	account.meta.monitor2_groups = [];
	return account;
}

export function monitor2EventGroup(account, eventUuid) {
	return monitor2Groups(account).find(g => g.event === eventUuid);
}

// Adds the event's views (MONITOR2_EVENT_VIEWS values) as videos in a group titled after the event
export function addMonitor2EventGroup(account, eventUuid, title, views) {
	var keys = views.map(view => monitor2EventKey(eventUuid, view));
	toggleMonitor2Items(account, keys, true);
	return addMonitor2Group(account, { title: title, event: eventUuid, items: keys });
}

// Removes the event's group; the videos inside it at that moment are removed by the monitor2 list
// when KVideoArea reports them ("group-removed"), so videos moved out of the group stay
export function removeMonitor2EventGroup(account, eventUuid) {
	var group = monitor2EventGroup(account, eventUuid);
	if (!group) return account;
	return removeMonitor2Group(account, group.id);
}

export function renameMonitor2Group(account, id, title) {
	account.meta.monitor2_groups = monitor2Groups(account).map(g => g.id === id ? Object.assign({}, g, { title: title }) : g);
	return account;
}

export function setMonitor2GroupColor(account, id, color) {
	account.meta.monitor2_groups = monitor2Groups(account).map(g => {
		if (g.id !== id) return g;
		var group = Object.assign({}, g);
		if (color) group.color = color;
		else delete group.color;
		return group;
	});
	return account;
}

export function removeMonitor2Group(account, id) {
	account.meta.monitor2_groups = monitor2Groups(account).filter(g => g.id !== id);
	return account;
}
